import { FunctionComponent } from "react";
import { Button, Form, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, FilePdfTwoTone, FilterOutlined, PauseCircleTwoTone, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { SelectionActionBar } from "../../../components/common/SelectionActionBar";
import { useNavigate } from "react-router-dom";
import { AdhesionLight } from "../../../services/adhesion";
import { StatutInscription } from "../../../services/inscription";
import { APPLICATION_DATE_FORMAT, APPLICATION_DATE_TIME_FORMAT } from "../../../utils/FormUtils";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { AdhesionViewProps } from "./types";
import { AdminSearchFilter } from "../../../components/common/AdminSearchFilter";
import { buildUrlWithParams, DOCUMENT_CONTENT_ENDPOINT } from "../../../services/services";

export const AdhesionDesktopView: FunctionComponent<AdhesionViewProps> = ({
    dataSource,
    selectedAdhesions,
    setSelectedAdhesions,
    onValidateAdhesion,
    onValidateAdhesions,
    onDeleteAdhesion,
    onDeleteAdhesions,
    onSearch,
    onExport,
}) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { token } = theme.useToken();

    const doSearch = async () => {
        const { nom, prenom, statut, montant } = form.getFieldsValue();
        let dateInscription = form.getFieldValue("dateInscription");
        if (dateInscription) {
            dateInscription = dateInscription.format(APPLICATION_DATE_FORMAT);
        }
        const searchCriteria = {
            nom: nom ?? null,
            prenom: prenom ?? null,
            statut: statut ?? null,
            dateInscription: dateInscription ?? null,
            montant: montant ?? null
        };
        await onSearch(searchCriteria);
    };

    const inputFiltersConfig = [
        { name: "prenom", libelle: "Prénom", inputType: "InputText" as const },
        { name: "nom", libelle: "Nom", inputType: "InputText" as const },
        { name: "montant", libelle: "Montant", inputType: "InputNumber" as const, tooltip: "Rechercher les ahdésions dont le montant est supérieur à" },
        { name: "dateInscription", libelle: "Date adhésion", inputType: "Date" as const, tooltip: "Rechercher les ahdésions reçues à partir du" },
        {
            name: "statut", libelle: "Statut", inputType: "Select" as const, selectOptions: [
                { value: StatutInscription.PROVISOIRE, label: "Provisoire" },
                { value: StatutInscription.VALIDEE, label: "Validée" }
            ]
        },
    ];

    const columnsTableAdhesions: ColumnsType<AdhesionLight> = [
        {
            title: 'Nom',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => a.nom.localeCompare(b.nom),
        },
        {
            title: 'Prénom',
            dataIndex: 'prenom',
            key: 'prenom',
        },
        {
            title: 'Montant versement',
            dataIndex: 'montant',
            key: 'montant',
            render: (value) => value ? `${value} €` : '-',
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (value) => {
                if (value === StatutInscription.VALIDEE)
                    return <Tag color="success" icon={<CheckCircleOutlined />}>Validée</Tag>;
                else
                    return <Tag color="warning" icon={<PauseCircleTwoTone />}>À valider</Tag>;
            }
        },
        {
            title: 'Date inscription',
            dataIndex: 'dateInscription',
            key: 'dateInscription',
            render: (value, record) => dayjs(record.dateInscription, APPLICATION_DATE_TIME_FORMAT).format(APPLICATION_DATE_FORMAT),
            sorter: (a, b) => dayjs(a.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix() - dayjs(b.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, adhesion: AdhesionLight) => (
                <Space size={4}>
                    <Tooltip title="Consulter">
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            style={{ color: "#1890ff", borderColor: "#1890ff" }}
                            onClick={() => navigate(`/adhesions/${adhesion.id}?readonly=true`)}
                            {...({ testid: `consulter-adhesion-${adhesion.id}` } as any)}
                        />
                    </Tooltip>
                    <Tooltip title="Modifier">
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            style={{ color: "#1890ff", borderColor: "#1890ff" }}
                            onClick={() => navigate(`/adhesions/${adhesion.id}?readonly=false`)}
                            {...({ testid: `modifier-adhesion-${adhesion.id}` } as any)}
                        />
                    </Tooltip>
                    <Tooltip title={adhesion.statut === StatutInscription.VALIDEE ? "Déjà validée" : "Valider"}>
                        <Button
                            icon={<CheckCircleOutlined />}
                            size="small"
                            style={{ color: adhesion.statut === StatutInscription.VALIDEE ? "#d9d9d9" : "#52c41a", borderColor: adhesion.statut === StatutInscription.VALIDEE ? "#d9d9d9" : "#52c41a" }}
                            disabled={adhesion.statut === StatutInscription.VALIDEE}
                            onClick={() => onValidateAdhesion(adhesion.id)}
                            {...({ testid: `valider-adhesion-${adhesion.id}` } as any)}
                        />
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => onDeleteAdhesion(adhesion.id)}
                            {...({ testid: `supprimer-adhesion-${adhesion.id}` } as any)}
                        />
                    </Tooltip>
                    {adhesion.documentPending ? (
                        <Tooltip title="Document en cours de génération, rafraîchissez les résultats dans quelques instants">
                            <Button
                                icon={<SyncOutlined spin />}
                                size="small"
                                disabled
                                {...({ testid: `document-pending-adhesion-${adhesion.id}` } as any)}
                            />
                        </Tooltip>
                    ) : adhesion.idDocument ? (
                        <Tooltip title="Télécharger PDF">
                            <Button
                                icon={<FilePdfTwoTone twoToneColor="#1890ff" />}
                                size="small"
                                style={{ color: "#1890ff", borderColor: "#1890ff" }}
                                onClick={() => {
                                    const url = buildUrlWithParams(DOCUMENT_CONTENT_ENDPOINT, { idDocument: adhesion.idDocument });
                                    window.open(`${process.env.REACT_APP_BASE_URL_API_V1}${url}`, '_blank');
                                }}
                                {...({ testid: `telecharger-pdf-adhesion-${adhesion.id}` } as any)}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Le document n'est pas encore disponible">
                            <Button
                                icon={<FilePdfTwoTone twoToneColor="#1890ff" />}
                                size="small"
                                style={{ color: "#1890ff", borderColor: "#1890ff" }}
                                disabled
                                {...({ testid: `document-indisponible-adhesion-${adhesion.id}` } as any)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: AdhesionLight[]) => {
            setSelectedAdhesions(selectedRows);
        }
    };

    const existAdhesions = () => {
        return dataSource && dataSource.length > 0;
    };

    const formatTotal = (total: number) => {
        if (total > 0) {
            return (<Tag color="geekblue">Total sélection : <strong>{total} adhésion(s)</strong></Tag>);
        } else {
            return (<Tag color="geekblue">Total sélection : <strong>{total}</strong></Tag>);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "row", gap: 16, alignItems: "flex-start" }}>
            {/* ── Panneau filtres ── */}
            <div
                style={{
                    background: token.colorBgContainer,
                    borderRadius: token.borderRadiusLG,
                    border: `1px solid ${token.colorBorder}`,
                    overflow: "hidden",
                    boxShadow: token.boxShadowSecondary,
                    width: 400,
                    flexShrink: 0,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 16px",
                        background: "#001529",
                        borderBottom: `1px solid ${token.colorBorder}`,
                    }}
                >
                    <SearchOutlined style={{ color: "#fff", fontSize: 14 }} />
                    <Typography.Text strong style={{ color: "#fff", fontSize: 14 }}>
                        Filtres de recherche
                    </Typography.Text>
                </div>
                <div style={{ padding: "16px 20px" }}>
                    <Form
                        name="adminAdhesionDesktop"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        autoComplete="off"
                        form={form}
                    >
                        <AdminSearchFilter doSearch={doSearch} inputFilters={inputFiltersConfig} />
                    </Form>
                </div>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                {dataSource && dataSource.length > 0 ? (
                    <div
                        style={{
                            background: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                            overflow: "hidden",
                            boxShadow: token.boxShadowSecondary,
                        }}
                    >
                        {/* En-tête résultats */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "12px 16px",
                                background: "#fafafa",
                                borderBottom: "1px solid #f0f0f0",
                                flexWrap: "wrap",
                                gap: 8,
                            }}
                        >
                            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                <strong style={{ color: "rgba(0,0,0,0.85)" }}>{dataSource.length}</strong>{" "}
                                adhésion{dataSource.length > 1 ? "s" : ""} trouvée{dataSource.length > 1 ? "s" : ""}
                            </Typography.Text>
                            <Tooltip title="Exporter tous les résultats dans un fichier Excel">
                                <Button
                                    icon={<FileExcelOutlined />}
                                    onClick={onExport}
                                    disabled={!existAdhesions()}
                                    style={{ color: "#52c41a", borderColor: "#52c41a" }}
                                >
                                    Exporter ({dataSource.length})
                                </Button>
                            </Tooltip>
                        </div>

                        {/* Barre d'actions groupées — uniquement si sélection active */}
                        {selectedAdhesions.length > 0 && (
                            <div style={{ padding: "8px 16px 0 16px" }}>
                                <SelectionActionBar
                                    selectedCount={selectedAdhesions.length}
                                    itemLabel="adhésion"
                                    onValidate={() => onValidateAdhesions(selectedAdhesions)}
                                    onDelete={() => onDeleteAdhesions(selectedAdhesions)}
                                    onCancel={() => setSelectedAdhesions([])}
                                />
                            </div>
                        )}

                        {/* Table */}
                        <div style={{ padding: "0 0 8px 0" }}>
                            <Table
                                rowSelection={{ type: "checkbox", selectedRowKeys: selectedAdhesions.map(adhesion => adhesion.id), ...rowSelection }}
                                columns={columnsTableAdhesions}
                                dataSource={dataSource}
                                rowKey={record => record.id}
                                size="middle"
                                scroll={{ x: "max-content" }}
                                pagination={{
                                    showSizeChanger: true,
                                    defaultPageSize: 10,
                                    pageSizeOptions: ["10", "20", "50"],
                                    showTotal: (total, range) => (
                                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                            {range[0]}–{range[1]} sur <strong>{total}</strong> résultat(s)
                                        </Typography.Text>
                                    ),
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "48px 24px",
                            background: "#fff",
                            borderRadius: 12,
                            border: "1px dashed #d9d9d9",
                            marginTop: 16,
                            gap: 8,
                        }}
                        role="status"
                        aria-live="polite"
                    >
                        <FilterOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                        <Typography.Text style={{ color: "#8c8c8c", fontSize: 15 }}>Aucune adhésion ne correspond aux filtres</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                            Modifiez les critères de recherche ci-dessus pour affiner les résultats.
                        </Typography.Text>
                    </div>
                )}
            </div>
        </div>
    );
};
